import {
    SignalsGroupByOptionLabels,
    type GroupByConfiguration,
} from "../../SignalsModels";

/** Leads the chip, so the row reads "Grouped by: Quality Assurance → Did the agent verify identity?". */
export const GROUPED_BY_CHIP_PREFIX = "Grouped by";

/**
 * The names behind a grouping's own selection, resolved by whoever holds the stores.
 *
 * Kept out of this module so the text itself is a pure function of the configuration and the names:
 * the same shape is resolved from MobX stores in the app and from the tenant database in the email
 * renderer, and neither belongs in the phrasing.
 */
export type GroupedByChipSelection = {
    /** Question Result's module, or Module Score's. */
    moduleName?: string;
    questionName?: string;
    modelName?: string;
    metadataFieldName?: string;
};

/**
 * What a chart's "Grouped by" chip says, or undefined when the grouping needs no chip.
 *
 * Only the four dimensions that are configured by picking something first get one. Grouping by
 * Agent or by Date is legible from the chart itself, but a chart grouped by a question's answers
 * shows "Yes / No / N/A" with nothing naming the question -- and the same bars could belong to any
 * question on any module.
 *
 * Every name is optional because the lists they come from load lazily, per chart. An unresolved
 * name falls back to the dimension's own menu label, so the chip appears at its final size as soon
 * as the chart is grouped and fills in detail rather than popping into existence later.
 */
export const getGroupedByChipText = (
    grouping: GroupByConfiguration | undefined,
    selection: GroupedByChipSelection,
): string | undefined => {
    if (!grouping) return undefined;

    switch (grouping.option) {
        case "QuestionResult":
            // "Module → Question", the same shape and arrow the Question Result filter chip uses
            // for the same pair. Both names or neither: a module with no question reads as a
            // grouping by the module, which is Module Score's chip, not this one.
            return selection.moduleName && selection.questionName
                ? `${selection.moduleName} → ${selection.questionName}`
                : SignalsGroupByOptionLabels.QuestionResult;
        case "ModelResults":
            // "<Model> Results", matching the Model Results filter chip.
            return selection.modelName
                ? `${selection.modelName} Results`
                : SignalsGroupByOptionLabels.ModelResults;
        case "ModuleScore":
            // "<Module> Score", matching the Module Scores filter chip.
            return selection.moduleName
                ? `${selection.moduleName} Score`
                : SignalsGroupByOptionLabels.ModuleScore;
        case "ExtendedMetadata":
            // The field's own name is the whole of it; there is no second level to phrase.
            return (
                selection.metadataFieldName ??
                SignalsGroupByOptionLabels.ExtendedMetadata
            );
        default:
            return undefined;
    }
};
